import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CreateUserBody,
  CreateUserData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface CreateUserState
  extends MergeRequestState<CreateUserData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.createUser,
  isFetching: (state: ReduxState) => state.server.createUser.isFetching,
  data: (state: ReduxState) => state.server.createUser.data,
  error: (state: ReduxState) => state.server.createUser.error,
};

const initialState: CreateUserState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/createUser';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    { body, config }: { body: CreateUserBody; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.createUser(body, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerCreateUser {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const createUser: ServerCreateUser = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
